// Demo exchange rates. Follows ledger.rate_for: a typed-in rate for the day
// wins, then the owner's bank feed (if connected, rates up to four days old),
// then the published source (ECB daily, or HMRC's monthly rate), then the other
// published source. Rates are base currency (euro) per one unit.
import { addMoney, isZero, mulMoney, negate } from '@elysian/ui';
import { foreignBankBalances } from './mockBank';
import { demoStore } from './mockDocuments';
import { CURRENCIES } from '../screens/shared/currencies';
import type { FxOverview, FxRate, LedgerSource, Revaluation } from './source';

type FxSource = Pick<LedgerSource, 'fxOverview' | 'setFxSettings' | 'fxRate' | 'setFxRate' | 'connectFxFeed'
  | 'disconnectFxFeed' | 'revaluationPreview' | 'postRevaluation'>;

const delay = <T,>(v: T, ms = 80): Promise<T> => new Promise((r) => setTimeout(() => r(structuredClone(v)), ms));
const fail = (detail: string) => Promise.reject(new Error(detail));

export const DEMO_TODAY = '2026-10-11';

export { CURRENCIES } from '../screens/shared/currencies';

/** ECB reference rates on 9 October 2026 as units per euro; the demo drifts them a little day by day. */
const PER_EURO: Record<string, number> = {
  AUD: 1.7712, CAD: 1.6048, CHF: 0.9336, CNY: 8.3124, CZK: 24.312, DKK: 7.4605, GBP: 0.8601, HKD: 9.0817,
  HUF: 393.41, INR: 98.12, JPY: 172.36, MXN: 21.744, NOK: 11.692, NZD: 2.0186, PLN: 4.2655, RON: 5.0812,
  SEK: 11.031, SGD: 1.5112, TRY: 48.72, USD: 1.1672, ZAR: 20.418,
  ISK: 152.1, BRL: 6.312, IDR: 19021, ILS: 4.351, KRW: 1621.4, MYR: 4.948, PHP: 66.02, THB: 38.47,
  // HMRC-only currencies (monthly rates in the demo too)
  RSD: 117.12, UAH: 48.52, BAM: 1.9558, MKD: 61.52, ALL: 98.11, MDL: 19.92, GEL: 3.152, TWD: 36.51, VND: 30512,
  BDT: 140.2, PKR: 328.4, AED: 4.287, SAR: 4.377, EGP: 56.48, MAD: 10.61, CLP: 1101.5, COP: 4702, ARS: 1352,
};
const ECB_DAILY = new Set(CURRENCIES.filter((x) => x.ecb_daily).map((x) => x.code));

const ANCHOR = Date.UTC(2026, 9, 9);
const dayNo = (d: string) => Math.round((Date.parse(`${d}T00:00:00Z`) - ANCHOR) / 86_400_000);
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
const addDays = (d: string, n: number) => iso(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000);
const fix = (n: number) => n.toFixed(6);

/** The ECB publishes on weekdays; a weekend uses Friday's rate. */
function ecbDay(d: string) {
  const wd = new Date(`${d}T00:00:00Z`).getUTCDay();
  return wd === 0 ? addDays(d, -2) : wd === 6 ? addDays(d, -1) : d;
}

function ecbRate(ccy: string, d: string): number | null {
  return ECB_DAILY.has(ccy) ? marketRate(ccy, d) : null;
}

function marketRate(ccy: string, d: string): number | null {
  const per = PER_EURO[ccy];
  if (!per || d > DEMO_TODAY) return null;
  const n = dayNo(ecbDay(d));
  return 1 / (per * (1 + 0.006 * Math.sin(n / 11) + 0.002 * Math.cos(n / 3)));
}

/** HMRC publishes one rate per month, set in the previous month. */
const hmrcRate = (ccy: string, d: string) => marketRate(ccy, addDays(`${d.slice(0, 7)}-01`, -10));

interface Feed { provider: 'wise' | 'revolut'; connected_at: string; last_fetched_at: string | null; last_error: string | null }

let source: 'ecb' | 'hmrc' = 'ecb';
let feed: Feed | null = null;
let manual: { currency: string; date: string; rate: number }[] = [];
let chosen: string[] = [];
let revalued: Record<string, { journal_id: string; posted_at: string; posted_by: string }> = {};

function seed() {
  source = 'ecb';
  feed = null;
  manual = [{ currency: 'GBP', date: '2026-09-30', rate: 1.1645 }];
  chosen = ['GBP', 'USD'];
  revalued = {};
}
seed();

export function resetDemoFx() {
  seed();
}

/** As ledger.rate_for. */
export function rateFor(ccy: string, date: string): FxRate | null {
  if (ccy === 'EUR') return { currency: 'EUR', rate: 1, source: 'base', rate_date: date };
  const typed = manual.find((m) => m.currency === ccy && m.date === date);
  if (typed) return { currency: ccy, rate: typed.rate, source: 'manual', rate_date: date };
  // A connected bank gives its own rate for recent days (it has no history further back).
  if (feed && !feed.last_error && date <= DEMO_TODAY && date >= addDays(DEMO_TODAY, -4)) {
    const r = ecbRate(ccy, date);
    if (r) return { currency: ccy, rate: Number(fix(r * (feed.provider === 'wise' ? 1.0007 : 1.0004))), source: feed.provider, rate_date: date };
  }
  const ecb = ecbRate(ccy, date);
  const hmrc = hmrcRate(ccy, date);
  const ecbOut = ecb && { currency: ccy, rate: Number(fix(ecb)), source: 'ecb' as const, rate_date: ecbDay(date) };
  const hmrcOut = hmrc && { currency: ccy, rate: Number(fix(hmrc)), source: 'hmrc' as const, rate_date: `${date.slice(0, 7)}-01` };
  return (source === 'ecb' ? ecbOut || hmrcOut : hmrcOut || ecbOut) || null;
}

/** For the demo documents: the rate as a string, or null when there is none. */
export function rateText(ccy: string, date: string): string | null {
  const r = rateFor(ccy, date);
  return r ? fix(r.rate) : null;
}

function overview(asAt = DEMO_TODAY): FxOverview {
  const used = new Set<string>();
  for (const d of demoStore.all()) if (d.currency !== 'EUR') used.add(d.currency);
  for (const c of demoStore.contacts) if (c.currency && c.currency !== 'EUR') used.add(c.currency);
  for (const a of demoStore.accounts) if (a.currency && a.currency !== 'EUR') used.add(a.currency);
  for (const m of manual) used.add(m.currency);
  return {
    base_currency: 'EUR', source, feed: feed?.provider ?? 'none',
    feed_connection: feed ? { ...feed } : null,
    chosen: [...chosen],
    currencies: [...chosen, ...[...used].filter((x) => !chosen.includes(x)).sort()].map((code) => ({
      code, name: CURRENCIES.find((c) => c.code === code)?.name ?? code, chosen: chosen.includes(code), in_use: used.has(code), today: rateFor(code, asAt),
    })),
    manual_rates: [...manual].sort((a, b) => b.date.localeCompare(a.date) || a.currency.localeCompare(b.currency)),
    all_currencies: CURRENCIES.map((x) => ({ ...x })),
  };
}

const money = (n: number) => n.toFixed(2);

/** As ledger.revaluation_preview: open foreign items and bank balances at the day's rate. */
function preview(asAt: string): Revaluation {
  const lines: Revaluation['lines'] = [];
  const missing = new Set<string>();
  const groups = new Map<string, { kind: 'debtors' | 'creditors'; contact_id: string; name: string; currency: string; amount: string; carried: string }>();
  for (const d of demoStore.open()) {
    if (d.currency === 'EUR' || d.issue_date > asAt) continue;
    const sales = d.type.startsWith('sales');
    const sign = (v: string) => (sales ? v : negate(v));
    const key = `${d.contact_id}|${d.currency}`;
    const g = groups.get(key) ?? { kind: sales ? 'debtors' : 'creditors', contact_id: d.contact_id, name: d.contact_name, currency: d.currency, amount: '0', carried: '0' };
    g.amount = addMoney(g.amount, sign(d.outstanding));
    g.carried = addMoney(g.carried, sign(mulMoney(d.outstanding, Number(d.fx_rate ?? '1').toFixed(6))));
    groups.set(key, g);
  }
  const add = (row: { kind: 'debtors' | 'creditors' | 'bank'; currency: string; amount: string; carried: string; name: string; contact_id?: string; account_code?: string }) => {
    const r = rateFor(row.currency, asAt);
    if (!r) { missing.add(row.currency); return; }
    const revaluedAt = mulMoney(row.amount, fix(r.rate));
    const difference = addMoney(revaluedAt, negate(row.carried));
    if (isZero(difference)) return;
    lines.push({ ...row, amount: Number(row.amount), carried: Number(row.carried), rate: r.rate, revalued: Number(revaluedAt), difference: Number(difference) });
  };
  groups.forEach((g) => add(g));
  for (const b of foreignBankBalances(asAt)) {
    if (isZero(b.amount) && isZero(b.carried)) continue;
    add({ kind: 'bank', account_code: b.code, name: b.name, currency: b.currency, amount: b.amount, carried: b.carried });
  }
  const total = Number(addMoney('0', ...lines.map((l) => money(l.difference))));
  return { as_at: asAt, lines, total, missing_rates: [...missing], already: revalued[asAt] ?? null };
}

export const demoFx: FxSource = {
  fxOverview: (asAt) => delay(overview(asAt)),
  setFxSettings: (s) => {
    if (s.currencies) {
      const unknown = s.currencies.map((x) => x.trim().toUpperCase()).find((x) => !CURRENCIES.some((c) => c.code === x));
      if (unknown) return fail(`${unknown} isn’t a currency the ledger knows`);
      chosen = [...new Set(s.currencies.map((x) => x.trim().toUpperCase()))].filter((x) => x !== 'EUR');
    }
    if (s.source) source = s.source;
    if (s.feed === 'none') feed = null;
    else if (s.feed && s.feed !== feed?.provider) return fail('Connect the bank first: it needs its API key.');
    return delay(overview());
  },
  fxRate: (ccy, date) => {
    const r = rateFor(ccy, date);
    return r ? delay(r) : fail(`There's no ${ccy} rate for ${date}. Type one in.`);
  },
  setFxRate: (ccy, date, rate) => {
    if (ccy === 'EUR') return fail('Euro is the base currency, so it has no rate.');
    if (rate !== null && !(rate > 0)) return fail('A rate must be more than zero.');
    manual = manual.filter((m) => !(m.currency === ccy && m.date === date));
    if (rate !== null) manual.push({ currency: ccy, date, rate });
    return delay(undefined);
  },
  connectFxFeed: (c) => {
    if (c.provider === 'wise' && c.token.trim().length < 20) return fail('That doesn’t look like a Wise API token. Create a read-only token under Settings → API tokens in Wise Business.');
    if (c.provider === 'revolut' && (!c.client_id.trim() || !c.refresh_token.trim() || !c.private_key_pem.includes('PRIVATE KEY') || !c.issuer.trim()))
      return fail('Revolut needs the client ID, the refresh token, the private key (PEM) and the redirect domain you registered.');
    feed = { provider: c.provider, connected_at: `${DEMO_TODAY}T10:00:00Z`, last_fetched_at: `${DEMO_TODAY}T10:00:05Z`, last_error: null };
    return delay(overview());
  },
  disconnectFxFeed: () => {
    feed = null;
    return delay(undefined);
  },
  revaluationPreview: (asAt) => delay(preview(asAt)),
  postRevaluation: (asAt) => {
    const p = preview(asAt);
    if (p.already) return fail(`Already revalued at ${asAt}.`);
    if (p.missing_rates.length) return fail(`No rate at ${asAt} for ${p.missing_rates.join(', ')}; type one in first.`);
    if (!p.lines.length) return fail('Nothing to revalue: every foreign balance is already at that day’s rate.');
    revalued[asAt] = { journal_id: `fxr-${asAt}`, posted_at: `${DEMO_TODAY}T10:30:00Z`, posted_by: 'ledger:user:brian' };
    return delay({ ...p, already: revalued[asAt] }, 120);
  },
};
